import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon, ArrowTopRightOnSquareIcon, ArrowUpTrayIcon,
  CheckCircleIcon, ChevronRightIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { openProSearch } from '../../utils/proSearch';
import accessorialImportService, {
  extractDetail,
  extractRowErrors,
  type AccessorialPreview,
  type AccessorialReferenceData,
  type ApplyResponse,
  type RowError,
  type ValidateResponse,
} from '../../services/accessorialImport.service';

/**
 * Accessorial Upload — 360 port of v1 imports/accessorialupload.pl.
 *
 * Download the 19-column form, fill one row per invoice LINE, upload it. Rows
 * are grouped by (PRO NUMBER, MAS TAG) into one accessorial invoice each.
 *
 * Two steps by design (#33737): the upload is validated and previewed first —
 * the user sees the exact invoices and totals about to be created — and only
 * Create writes. Any validation error rejects the whole file; nothing is ever
 * written partially.
 */
const AccessorialUploadPage: React.FC = () => {
  const [ref, setRef] = useState<AccessorialReferenceData | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<'validate' | 'apply' | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<RowError[]>([]);
  const [preview, setPreview] = useState<ValidateResponse | null>(null);
  const [result, setResult] = useState<ApplyResponse | null>(null);
  // Review an order the import just touched without leaving the results.
  const [drawerPro, setDrawerPro] = useState<number | null>(null);
  // Group keys the user has unticked. Duplicates start in here — creating one
  // should take a deliberate tick, not inattention.
  const [skipped, setSkipped] = useState<Set<string>>(new Set());

  useEffect(() => {
    accessorialImportService.getReferenceData().then(setRef).catch(() => setRef(null));
  }, []);

  const clearFeedback = () => { setErrorMsg(null); setRowErrors([]); };

  const onPickFile = (f: File | null) => {
    setFile(f);
    setPreview(null);
    setResult(null);
    setSkipped(new Set());
    clearFeedback();
  };

  const onDownloadForm = async () => {
    clearFeedback();
    try {
      await accessorialImportService.downloadForm();
    } catch (e) {
      setErrorMsg(extractDetail(e));
    }
  };

  const onValidate = async () => {
    if (!file) return;
    clearFeedback();
    setResult(null);
    setBusy('validate');
    try {
      const res = await accessorialImportService.validate(file);
      setPreview(res);
      setSkipped(new Set(
        res.accessorials.filter((a) => a.duplicate_of).map((a) => a.key),
      ));
    } catch (e) {
      setPreview(null);
      const errs = extractRowErrors(e);
      if (errs.length) setRowErrors(errs); else setErrorMsg(extractDetail(e));
    } finally {
      setBusy(null);
    }
  };

  const onApply = async () => {
    if (!file) return;
    clearFeedback();
    setBusy('apply');
    try {
      const res = await accessorialImportService.apply(file, [...skipped]);
      setResult(res);
      setPreview(null);
    } catch (e) {
      const errs = extractRowErrors(e);
      if (errs.length) setRowErrors(errs); else setErrorMsg(extractDetail(e));
    } finally {
      setBusy(null);
    }
  };

  const startOver = () => {
    onPickFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="p-[18px] max-w-[1100px]">
      <div className="flex items-center gap-1.5 mb-3">
        <ArrowUpTrayIcon className="h-5 w-5 text-accent-text" />
        <h1 className="m-0 text-[0.9375rem] font-bold text-heading">Accessorial Upload</h1>
      </div>

      <section className="bg-panel border border-rule rounded-lg p-4 mb-3">
        <div className="text-[0.75rem] font-bold text-label mb-2">1. Get the form</div>
        <p className="text-[0.75rem] text-muted mb-3 max-w-[760px]">
          One row per invoice <b>line</b>. Rows sharing a <b>PRO NUMBER</b> and{' '}
          <b>MAS TAG</b> become a single accessorial invoice, so the invoice-level
          columns (Bill To Ref#, Invoice Comments, Quote#, and the paperwork flags)
          must match on every row of that group.
        </p>
        <button
          type="button"
          onClick={onDownloadForm}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-[0.75rem] font-semibold text-accent-text border border-accent rounded hover:bg-highlight"
        >
          <ArrowDownTrayIcon className="h-4 w-4" /> Download Accessorial Form
        </button>
        {ref && <RequiredColumnHelp refData={ref} />}
      </section>

      <section className="bg-panel border border-rule rounded-lg p-4 mb-3">
        <div className="text-[0.75rem] font-bold text-label mb-2">2. Upload &amp; review</div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            aria-label="Accessorial spreadsheet"
            onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            className="text-[0.75rem] text-body"
          />
          <button
            type="button"
            onClick={onValidate}
            disabled={!file || busy !== null}
            className="px-3 py-1.5 text-[0.75rem] font-semibold text-accent-fg bg-accent border border-accent rounded disabled:opacity-40 hover:bg-accent-hover"
          >
            {busy === 'validate' ? 'Checking…' : 'Review'}
          </button>
          <span className="text-[0.6875rem] text-muted">.xlsx, .xls or .csv</span>
        </div>

        {errorMsg && (
          <div className="mt-3 px-3 py-2 text-[0.75rem] text-danger-fg bg-danger border border-rule rounded">
            {errorMsg}
          </div>
        )}
        {rowErrors.length > 0 && <ErrorList errors={rowErrors} />}
      </section>

      {preview && (
        <PreviewPanel
          preview={preview}
          skipped={skipped}
          onToggle={(key) => setSkipped((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
          })}
          busy={busy === 'apply'}
          onCreate={onApply}
          onCancel={startOver}
        />
      )}

      {result && (
        <ResultPanel result={result} onReset={startOver} onOpenPro={setDrawerPro} />
      )}

      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={() => setDrawerPro(null)}
      />
    </div>
  );
};

// --- format help -------------------------------------------------------------

const RequiredColumnHelp: React.FC<{ refData: AccessorialReferenceData }> = ({ refData }) => (
  <details className="mt-3">
    <summary className="text-[0.75rem] text-accent-text cursor-pointer select-none">
      Expected columns and accepted values
    </summary>
    <div className="mt-2 text-[0.6875rem] text-muted space-y-1.5">
      <div>
        <b className="text-label">Required</b> — {refData.required_columns.join(', ')}
      </div>
      <div>
        <b className="text-label">All columns</b> — {refData.columns.join(', ')}
      </div>
      <div>
        <b className="text-label">TYPE</b> — {refData.rate_types.join(', ')} (or blank).
        A <code>PCT %</code> line bills (Qty × Rate) ÷ 100.
      </div>
      <div>
        <b className="text-label">Paperwork flags</b> — {refData.paperwork_flags.join(', ')}{' '}
        (or blank; Pending Approval defaults to Y, the rest to N).
      </div>
      <div>
        <b className="text-label">MAS CODE</b> — {refData.all_gl_codes.join(', ')}.
        For the item <code>{refData.dtd_item_name}</code> use the /WHSE or /REGION
        variants instead.
      </div>
    </div>
  </details>
);

const ErrorList: React.FC<{ errors: RowError[] }> = ({ errors }) => (
  <div className="mt-3 px-3 py-2 text-[0.75rem] text-danger-fg bg-danger border border-rule rounded">
    <div className="font-semibold mb-1">
      Nothing was imported. Fix {errors.length === 1 ? 'this' : `these ${errors.length}`} and
      upload again:
    </div>
    <ul className="list-disc ml-5 space-y-0.5 max-h-[260px] overflow-auto">
      {errors.map((e, i) => (
        <li key={i}>{e.row ? `Row ${e.row}: ` : ''}{e.message}</li>
      ))}
    </ul>
  </div>
);

// --- preview -----------------------------------------------------------------

const PreviewPanel: React.FC<{
  preview: ValidateResponse;
  skipped: Set<string>;
  onToggle: (key: string) => void;
  busy: boolean;
  onCreate: () => void;
  onCancel: () => void;
}> = ({ preview, skipped, onToggle, busy, onCreate, onCancel }) => {
  const selected = preview.accessorials.filter((a) => !skipped.has(a.key));
  const total = selected.reduce((sum, a) => sum + Number(a.total), 0);
  return (
    <section className="bg-panel border border-rule rounded-lg p-4 mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[0.75rem] font-bold text-label">
          3. Review — nothing has been created yet
        </div>
        <div className="text-[0.75rem] text-muted">{preview.filename}</div>
      </div>

      <div className="flex flex-wrap gap-4 text-[0.75rem] text-body mb-3">
        <span><b>{preview.invoice_count}</b> accessorial {preview.invoice_count === 1 ? 'invoice' : 'invoices'}</span>
        <span><b>{preview.line_count}</b> line {preview.line_count === 1 ? 'item' : 'items'}</span>
        <span>from <b>{preview.total_rows}</b> rows</span>
        <span className="ml-auto">Selected <b>{money(String(total))}</b></span>
      </div>

      {preview.duplicate_count > 0 && (
        <div
          role="status"
          data-testid="duplicate-banner"
          className="flex items-start gap-2 mb-3 px-3 py-2 text-[0.75rem] text-warn-fg bg-warn border border-rule rounded"
        >
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-px" />
          <span>
            <b>{preview.duplicate_count}</b>{' '}
            {preview.duplicate_count === 1 ? 'invoice already exists' : 'invoices already exist'}{' '}
            on {preview.duplicate_count === 1 ? 'its order' : 'their orders'} with the same
            charges, so {preview.duplicate_count === 1 ? 'it has' : 'they have'} been unticked.
            Tick {preview.duplicate_count === 1 ? 'it' : 'them'} to bill again anyway.
          </span>
        </div>
      )}

      <div className="max-h-[420px] overflow-auto border border-hairline rounded mb-3">
        {preview.accessorials.map((a) => (
          <AccessorialCard
            key={a.key}
            acc={a}
            skipped={skipped.has(a.key)}
            onToggle={() => onToggle(a.key)}
          />
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onCreate}
          disabled={busy || selected.length === 0}
          className="px-3 py-1.5 text-[0.75rem] font-semibold text-accent-fg bg-accent border border-accent rounded disabled:opacity-40 hover:bg-accent-hover"
        >
          {busy
            ? 'Creating…'
            : `Create ${selected.length} ${selected.length === 1 ? 'Invoice' : 'Invoices'}`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1.5 text-[0.75rem] font-medium text-muted border border-rule rounded disabled:opacity-40 hover:bg-highlight"
        >
          Cancel
        </button>
        {skipped.size > 0 && (
          <span className="text-[0.75rem] text-muted">
            {skipped.size} skipped
          </span>
        )}
      </div>
    </section>
  );
};

const AccessorialCard: React.FC<{
  acc: AccessorialPreview;
  skipped: boolean;
  onToggle: () => void;
}> = ({ acc, skipped, onToggle }) => (
  <details className="border-b border-hairline last:border-b-0" open={false}>
    <summary className={`flex flex-wrap items-center gap-3 px-3 py-2 text-[0.75rem] cursor-pointer select-none bg-sunken hover:bg-highlight ${skipped ? 'opacity-55' : ''}`}>
      <ChevronRightIcon className="h-3.5 w-3.5 text-muted shrink-0" />
      {/* Inside a <summary>, so the click must not also toggle the disclosure. */}
      <input
        type="checkbox"
        checked={!skipped}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Create the accessorial for PRO ${acc.pronumber}, MAS tag ${acc.mas_tag}`}
      />
      <span className="font-semibold text-body">PRO {acc.pronumber}</span>
      <span className="text-muted">MAS TAG</span>
      <span className="font-medium text-body">{acc.mas_tag}</span>
      <span className="text-muted truncate max-w-[240px]">{acc.billto_name}</span>
      {acc.quote_number > 0 && (
        <span className="text-muted">Quote {acc.quote_number}-{acc.revision_number}</span>
      )}
      {acc.duplicate_of && (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-warn-fg bg-warn"
          title={
            `Accessorial #${acc.duplicate_of.accessorial_num} on this order already has these `
            + `charges (${money(acc.duplicate_of.total)})`
            + (acc.duplicate_of.created_at ? `, created ${acc.duplicate_of.created_at}` : '')
            + (acc.duplicate_of.created_by ? ` by ${acc.duplicate_of.created_by}` : '')
          }
        >
          <ExclamationTriangleIcon className="h-3 w-3" />
          already on acc #{acc.duplicate_of.accessorial_num}
        </span>
      )}
      <span className="ml-auto text-muted">
        {acc.line_count} {acc.line_count === 1 ? 'line' : 'lines'}
      </span>
      <span className="font-semibold text-body tabular-nums w-[104px] text-right">
        {money(acc.total)}
      </span>
    </summary>
    <table className="w-full text-[0.6875rem]">
      <thead className="text-muted">
        <tr>
          <Th>Row</Th><Th>#</Th><Th>Item</Th><Th>MAS Code</Th>
          <Th right>Qty</Th><Th right>Rate</Th><Th>Type</Th><Th>Description</Th>
          <Th right>Total</Th>
        </tr>
      </thead>
      <tbody>
        {acc.line_items.map((li) => (
          <tr key={li.row} className="border-t border-hairline">
            <Td className="text-muted">{li.row}</Td>
            <Td className="text-muted">{li.line_number}</Td>
            <Td className="font-medium text-body">
              {li.item}
              {li.item_label !== li.item && (
                <span className="text-muted"> (sheet: {li.item_label})</span>
              )}
            </Td>
            <Td>{li.gl_code}</Td>
            <Td right className="tabular-nums">{li.qty}</Td>
            <Td right className="tabular-nums">{li.rate}</Td>
            <Td>{li.rate_type}</Td>
            <Td className="text-muted">{li.description}</Td>
            <Td right className="tabular-nums font-medium text-body">{money(li.total)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  </details>
);

// --- result ------------------------------------------------------------------

const ResultPanel: React.FC<{
  result: ApplyResponse;
  onReset: () => void;
  onOpenPro: (pro: number) => void;
}> = ({ result, onReset, onOpenPro }) => (
  <section className="bg-panel border border-rule rounded-lg p-4">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-1.5">
        <CheckCircleIcon className="h-4 w-4 text-ok-fg" />
        <span className="text-[0.75rem] font-bold text-label">
          Created {result.invoice_count} {result.invoice_count === 1 ? 'invoice' : 'invoices'} and{' '}
          {result.line_count} line {result.line_count === 1 ? 'item' : 'items'} — {money(result.grand_total)}
          {result.skipped_count > 0 && (
            <span className="font-normal text-muted">
              {' '}({result.skipped_count} skipped)
            </span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-4">
        {/* Every PRO the run touched, as one search-results tab — the quickest way
            to eyeball a whole batch. Deduped: a PRO can carry several accessorials. */}
        <button
          type="button"
          onClick={() => openProSearch([...new Set(result.created.map((c) => c.pronumber))])}
          className="inline-flex items-center gap-1 text-[0.75rem] font-medium text-accent-text hover:underline"
        >
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          Open all {new Set(result.created.map((c) => c.pronumber)).size} PROs in search results
        </button>
        <button type="button" onClick={onReset} className="text-[0.75rem] font-medium text-accent-text hover:underline">
          Upload another file
        </button>
      </div>
    </div>
    <div className="max-h-[360px] overflow-auto border border-hairline rounded">
      <table className="w-full text-[0.6875rem]">
        <thead className="sticky top-0 bg-sunken text-muted">
          <tr>
            <Th>PRO</Th><Th>MAS Tag</Th><Th>Accessorial #</Th>
            <Th right>Lines</Th><Th right>Total</Th>
          </tr>
        </thead>
        <tbody>
          {result.created.map((c) => (
            <tr key={c.recno} className="border-t border-hairline">
              <Td>
                <button
                  type="button"
                  onClick={() => onOpenPro(c.pronumber)}
                  className="font-medium text-accent-text hover:underline"
                  title="Open this order for review"
                >
                  {c.pronumber}
                </button>
              </Td>
              <Td>{c.mas_tag}</Td>
              <Td>{c.accessorial_num}</Td>
              <Td right className="tabular-nums">{c.line_count}</Td>
              <Td right className="tabular-nums font-medium text-body">{money(c.total)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

// --- bits --------------------------------------------------------------------

const Th: React.FC<{ children: React.ReactNode; right?: boolean }> = ({ children, right }) => (
  <th className={`px-2 py-1 font-semibold ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

const Td: React.FC<{ children: React.ReactNode; right?: boolean; className?: string }> = ({
  children, right, className = '',
}) => (
  <td className={`px-2 py-1 ${right ? 'text-right' : 'text-left'} ${className}`}>{children}</td>
);

/** Amounts arrive as already-quantized decimal strings — format, never re-round. */
function money(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default AccessorialUploadPage;
